import { GoalTemplate, WebPageEvents } from '@griply/types';
import { getLocalizedStringValue } from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalTemplatePreviewActionPlan } from 'shared/components/ui/goal-template-preview-action-plan';
import { GoalTemplatePreviewDialogFooter } from 'shared/components/ui/goal-template-preview-dialog-footer';
import { GoalTemplatePreviewDialogHeader } from 'shared/components/ui/goal-template-preview-dialog-header';
import { GoalTemplatePreviewHeader } from 'shared/components/ui/goal-template-preview-header';
import { GoalTemplatePreviewProgress } from 'shared/components/ui/goal-template-preview-progress';
import { Section } from 'shared/components/ui/section';
import { useNumberFormat } from 'shared/hooks/use-number-format';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { LifeArea } from 'shared/types/life-area';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './goal-template-preview.style';

export type GoalTemplatePreviewProps = {
  template: GoalTemplate;
  onSubmit: (template: GoalTemplate) => void;
  onCancel: () => void;

  lifeAreas: LifeArea[];
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
};

export const GoalTemplatePreview: React.FC<GoalTemplatePreviewProps> = ({
  template,
  onSubmit,
  onCancel,

  lifeAreas,
  weekStartsOn,
  timeFormat,
}) => {
  useTrackPageVisitEvent(WebPageEvents.GoalTemplatePreview, undefined);
  const { t } = useTranslation();
  const locale = useI18nLocale();

  const submit = () => onSubmit(template);

  const goal = useMemo(
    () => template.goals.find(({ isMain }) => isMain) ?? template.goals[0],
    [template],
  );

  const numberFormat = useNumberFormat();

  return (
    <>
      <GoalTemplatePreviewDialogHeader
        name={t('forms.goal-template-preview.title')}
      />
      <Styled.Content>
        <GoalTemplatePreviewHeader
          name={getLocalizedStringValue(goal.name, locale)}
          iconName={goal.iconName}
          lifeAreaTemplateId={goal.lifeAreaTemplateId}
          daysTillDeadline={goal.daysTillDeadline}
          lifeAreas={lifeAreas}
        />

        <GoalTemplatePreviewProgress
          metric={goal.metric}
          daysTillDeadline={goal.daysTillDeadline}
          totalTaskCount={template.tasks.length}
          // todo: figure out how we will do this in the future, as we can support multiple main-goals in one template
          totalSubGoalCount={template.goals.length - 1}
          weekStartsOn={weekStartsOn}
          numberFormat={numberFormat}
        />

        <Section title={t('forms.goal-template-preview.action-plan.title')}>
          <GoalTemplatePreviewActionPlan
            mainGoal={goal}
            goals={template.goals}
            tasks={template.tasks}
            lifeAreas={lifeAreas}
            timeFormat={timeFormat}
          />
        </Section>
      </Styled.Content>
      <GoalTemplatePreviewDialogFooter onCancel={onCancel} onSubmit={submit} />
    </>
  );
};
